import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Home, Activity, Shield, Target, Zap, Users, Heart, Brain, TrendingUp, ArrowLeft, Clock, AlertTriangle, FileText, QrCode } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { Link } from 'wouter';

export default function MicrodosingGLP1s() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { isSignedIn } = useUser();

  if (!isSignedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">Please sign in to access this lecture.</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        prevSlide();
      } else if (event.key === 'ArrowRight') {
        nextSlide();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentSlide]);

  const slides = [
    // Slide 1: GLP-1 RAs & the SHIFT of Longevity
    {
      title: "GLP-1 RAs & the SHIFT of Longevity",
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center space-y-8">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl font-light text-gray-900 leading-tight mb-6">
              Microdosing GLP-1s
            </h1>
            <h2 className="text-2xl font-medium text-blue-600 mb-8">
              The SHIFT Framework Approach to GLP-1 Receptor Agonists
            </h2>
            
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-8 rounded-lg mb-8">
              <p className="text-xl font-light italic text-gray-800">
                "Weight-loss headlines missed the real story: these peptides turn back the cellular clock."
              </p>
            </div>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 500 300" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              {/* Gut-brain axis morphing into stopwatch */}
              <defs>
                <linearGradient id="gutBrainGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#8b5cf6" />
                </linearGradient>
              </defs>
              
              {/* Gut */}
              <path d="M 100 150 Q 120 120 140 130 T 160 150 Q 180 170 160 190 T 140 210 Q 120 190 100 200 T 80 180 Q 60 160 80 140 T 100 150" 
                    fill="none" stroke="url(#gutBrainGradient)" strokeWidth="3" />
              
              {/* Connection line */}
              <path d="M 160 150 Q 250 150 340 150" fill="none" stroke="#6b7280" strokeWidth="2" strokeDasharray="5,5" />
              
              {/* Brain */}
              <ellipse cx="400" cy="150" rx="60" ry="50" fill="none" stroke="url(#gutBrainGradient)" strokeWidth="3" />
              <path d="M 360 150 Q 380 130 400 140 T 420 150 Q 440 160 420 170 T 400 180 Q 380 170 360 160" 
                    fill="none" stroke="url(#gutBrainGradient)" strokeWidth="2" />
              
              {/* Stopwatch overlay */}
              <circle cx="400" cy="150" r="70" fill="none" stroke="#ef4444" strokeWidth="3" strokeDasharray="10,5" opacity="0.7" />
              <line x1="400" y1="150" x2="400" y2="100" stroke="#ef4444" strokeWidth="3" />
              <line x1="400" y1="150" x2="430" y2="130" stroke="#ef4444" strokeWidth="2" />
              
              <text x="250" y="250" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Gut-Brain Axis → Cellular Time Control
              </text>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 2: Why This Matters Now
    {
      title: "Why This Matters Now",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-xl font-medium text-blue-800 mb-4">The GLP-1 Revolution</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                <p className="text-lg text-gray-700">
                  <strong>&gt; 70 million</strong> US adults eligible for GLP-1s
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                <p className="text-lg text-gray-700">
                  First drug class to cut cardiovascular death in <strong>non-diabetics</strong>
                </p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
            <h3 className="text-xl font-medium text-green-800 mb-3">Paradigm Shift</h3>
            <p className="text-lg text-gray-700">
              Longevity medicine finally has a tool that changes hard outcomes within a single presidential term.
            </p>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 500 250" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="250" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                GLP-1 Impact Timeline
              </text>
              
              {/* Timeline */}
              <line x1="50" y1="100" x2="450" y2="100" stroke="#6b7280" strokeWidth="2" />
              
              {/* Markers */}
              <circle cx="150" cy="100" r="8" fill="#3b82f6" />
              <text x="150" y="130" textAnchor="middle" className="text-xs fill-gray-600">Week 1-4</text>
              <text x="150" y="145" textAnchor="middle" className="text-xs fill-gray-600">Glucose control</text>
              
              <circle cx="250" cy="100" r="8" fill="#22c55e" />
              <text x="250" y="130" textAnchor="middle" className="text-xs fill-gray-600">Month 3-6</text>
              <text x="250" y="145" textAnchor="middle" className="text-xs fill-gray-600">Weight loss</text>
              
              <circle cx="350" cy="100" r="8" fill="#ef4444" />
              <text x="350" y="130" textAnchor="middle" className="text-xs fill-gray-600">Year 1-2</text>
              <text x="350" y="145" textAnchor="middle" className="text-xs fill-gray-600">CV mortality ↓</text>
              
              {/* Impact box */}
              <rect x="100" y="170" width="300" height="60" fill="#fef3c7" stroke="#f59e0b" strokeWidth="1" rx="5" />
              <text x="250" y="195" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Rapid, Measurable Impact
              </text>
              <text x="250" y="215" textAnchor="middle" className="text-xs fill-gray-600">
                Unlike other longevity interventions
              </text>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 3: SHIFT Framework in 60s
    {
      title: "SHIFT Framework in 60 s",
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg">
            <h3 className="text-2xl font-medium text-gray-800 mb-4">The SHIFT Framework</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-4">
                <span className="text-3xl font-bold text-blue-600">S</span>
                <span className="text-lg text-gray-700">ensing</span>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-3xl font-bold text-green-600">H</span>
                <span className="text-lg text-gray-700">omeostatic-defence</span>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-3xl font-bold text-yellow-600">I</span>
                <span className="text-lg text-gray-700">nflammation</span>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-3xl font-bold text-red-600">F</span>
                <span className="text-lg text-gray-700">ibrosis</span>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-3xl font-bold text-purple-600">T</span>
                <span className="text-lg text-gray-700">ime-keeping</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <p className="text-center text-lg text-gray-700">
              Each pillar = modifiable lever tracked with routine labs
            </p>
            <p className="text-center text-xl font-medium text-blue-600 mt-2">
              (GLP-1s pull every lever)
            </p>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 500 300" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="250" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                SHIFT Framework: All Levers Engaged
              </text>
              
              {/* Central GLP-1 */}
              <circle cx="250" cy="150" r="40" fill="#dbeafe" stroke="#3b82f6" strokeWidth="3" />
              <text x="250" y="155" textAnchor="middle" className="text-sm font-bold fill-gray-700">GLP-1</text>
              
              {/* Five pillars */}
              <g transform="translate(250, 60)">
                <circle cx="0" cy="0" r="30" fill="#dcfce7" stroke="#22c55e" strokeWidth="2" />
                <text x="0" y="5" textAnchor="middle" className="text-xs font-medium">S</text>
              </g>
              
              <g transform="translate(340, 110)">
                <circle cx="0" cy="0" r="30" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" />
                <text x="0" y="5" textAnchor="middle" className="text-xs font-medium">H</text>
              </g>
              
              <g transform="translate(340, 190)">
                <circle cx="0" cy="0" r="30" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2" />
                <text x="0" y="5" textAnchor="middle" className="text-xs font-medium">I</text>
              </g>
              
              <g transform="translate(160, 190)">
                <circle cx="0" cy="0" r="30" fill="#fecaca" stroke="#ef4444" strokeWidth="2" />
                <text x="0" y="5" textAnchor="middle" className="text-xs font-medium">F</text>
              </g>
              
              <g transform="translate(160, 110)">
                <circle cx="0" cy="0" r="30" fill="#e9d5ff" stroke="#a855f7" strokeWidth="2" />
                <text x="0" y="5" textAnchor="middle" className="text-xs font-medium">T</text>
              </g>
              
              {/* Connections */}
              <line x1="250" y1="110" x2="250" y2="90" stroke="#6b7280" strokeWidth="2" />
              <line x1="280" y1="130" x2="310" y2="110" stroke="#6b7280" strokeWidth="2" />
              <line x1="280" y1="170" x2="310" y2="190" stroke="#6b7280" strokeWidth="2" />
              <line x1="220" y1="170" x2="190" y2="190" stroke="#6b7280" strokeWidth="2" />
              <line x1="220" y1="130" x2="190" y2="110" stroke="#6b7280" strokeWidth="2" />
            </svg>
          </div>
        </div>
      )
    },

    // Slide 4: SHIFT vs "Hallmarks"
    {
      title: "SHIFT vs \"Hallmarks\"",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-red-800 mb-3">Hallmarks of Aging</h3>
              <ul className="text-sm space-y-2 text-gray-700">
                <li>• Describe decay</li>
                <li>• Academic framework</li>
                <li>• Limited clinical utility</li>
              </ul>
            </div>
            
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-green-800 mb-3">SHIFT Framework</h3>
              <ul className="text-sm space-y-2 text-gray-700">
                <li>• Describes defence</li>
                <li>• Biomarker-anchored</li>
                <li>• Clinically actionable</li>
              </ul>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Pillar ↔ Exemplar Marker</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Pillar</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Biomarker</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Clinical Relevance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-2 text-sm text-gray-700">Sensing</td>
                    <td className="px-4 py-2 text-sm text-gray-600">HbA1c, Insulin</td>
                    <td className="px-4 py-2 text-sm text-gray-600">Metabolic control</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm text-gray-700">Homeostatic</td>
                    <td className="px-4 py-2 text-sm text-gray-600">Lipid panel, BP</td>
                    <td className="px-4 py-2 text-sm text-gray-600">CV risk</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm text-gray-700">Inflammation</td>
                    <td className="px-4 py-2 text-sm text-gray-600">hs-CRP</td>
                    <td className="px-4 py-2 text-sm text-gray-600">Systemic inflammation</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm text-gray-700">Fibrosis</td>
                    <td className="px-4 py-2 text-sm text-gray-600">FibroScan, ALT</td>
                    <td className="px-4 py-2 text-sm text-gray-600">Tissue damage</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm text-gray-700">Time-keeping</td>
                    <td className="px-4 py-2 text-sm text-gray-600">HRV, Cortisol rhythm</td>
                    <td className="px-4 py-2 text-sm text-gray-600">Circadian health</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )
    },

    // Slide 5: S — Nutrient Radar Reset
    {
      title: "S — Sensing: Nutrient Radar Reset",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">GLP-1 Sensing Mechanisms</h3>
            <ul className="text-sm space-y-2 text-gray-700">
              <li>• GLP-1 enhances L-cell meal signal</li>
              <li>• Re-tunes hypothalamic satiety circuits</li>
              <li>• Retatrutide triples the signal via GIP + glucagon co-activation</li>
            </ul>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 500 350" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="250" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Nutrient Sensing Enhancement
              </text>
              
              {/* Intestine */}
              <rect x="50" y="60" width="120" height="200" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2" rx="10" />
              <text x="110" y="85" textAnchor="middle" className="text-xs font-medium fill-gray-700">Intestine</text>
              
              {/* L-cells */}
              <circle cx="90" cy="120" r="15" fill="#3b82f6" />
              <text x="90" y="125" textAnchor="middle" className="text-xs fill-white">L</text>
              <circle cx="130" cy="160" r="15" fill="#3b82f6" />
              <text x="130" y="165" textAnchor="middle" className="text-xs fill-white">L</text>
              <circle cx="90" cy="200" r="15" fill="#3b82f6" />
              <text x="90" y="205" textAnchor="middle" className="text-xs fill-white">L</text>
              
              {/* Signals */}
              <path d="M 170 120 Q 250 100 330 120" fill="none" stroke="#3b82f6" strokeWidth="2" markerEnd="url(#arrowBlue)" />
              <text x="250" y="110" textAnchor="middle" className="text-xs fill-gray-600">GLP-1</text>
              
              <path d="M 170 160 Q 250 150 330 160" fill="none" stroke="#22c55e" strokeWidth="2" markerEnd="url(#arrowGreen)" />
              <text x="250" y="150" textAnchor="middle" className="text-xs fill-gray-600">GIP</text>
              
              <path d="M 170 200 Q 250 190 330 200" fill="none" stroke="#ef4444" strokeWidth="2" markerEnd="url(#arrowRed)" />
              <text x="250" y="190" textAnchor="middle" className="text-xs fill-gray-600">Glucagon</text>
              
              {/* Brain */}
              <ellipse cx="380" cy="160" rx="60" ry="50" fill="#e9d5ff" stroke="#a855f7" strokeWidth="2" />
              <text x="380" y="140" textAnchor="middle" className="text-xs font-medium fill-gray-700">Hypothalamus</text>
              <text x="380" y="155" textAnchor="middle" className="text-xs fill-gray-600">Satiety</text>
              <text x="380" y="170" textAnchor="middle" className="text-xs fill-gray-600">Circuits</text>
              
              {/* Enhancement box */}
              <rect x="150" y="270" width="200" height="50" fill="#dcfce7" stroke="#22c55e" strokeWidth="2" rx="5" />
              <text x="250" y="290" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Retatrutide: 3× Signal
              </text>
              <text x="250" y="305" textAnchor="middle" className="text-xs fill-gray-600">
                Triple receptor activation
              </text>
              
              <defs>
                <marker id="arrowBlue" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#3b82f6" />
                </marker>
                <marker id="arrowGreen" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#22c55e" />
                </marker>
                <marker id="arrowRed" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#ef4444" />
                </marker>
              </defs>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 6: Evidence: Retatrutide Phase 2
    {
      title: "Evidence: Retatrutide Phase 2",
      content: (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">48-Week Results</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-3 rounded">
                <p className="text-2xl font-bold text-green-600">−24 kg</p>
                <p className="text-sm text-gray-600">Mean weight loss</p>
              </div>
              <div className="bg-white p-3 rounded">
                <p className="text-2xl font-bold text-green-600">−17.5%</p>
                <p className="text-sm text-gray-600">Body weight reduction</p>
              </div>
            </div>
            <p className="text-sm text-gray-700 mt-3">
              <strong>Key finding:</strong> No plateau → durable set-point shift
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Metabolic Improvements</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-3 rounded">
                <p className="text-xl font-bold text-blue-600">↓40%</p>
                <p className="text-sm text-gray-600">Fasting insulin</p>
              </div>
              <div className="bg-white p-3 rounded">
                <p className="text-xl font-bold text-blue-600">↓45%</p>
                <p className="text-sm text-gray-600">Leptin</p>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 500 300" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="250" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Retatrutide Weight Loss Trajectory
              </text>
              
              {/* Axes */}
              <line x1="60" y1="240" x2="440" y2="240" stroke="#6b7280" strokeWidth="2" />
              <line x1="60" y1="240" x2="60" y2="60" stroke="#6b7280" strokeWidth="2" />
              
              {/* Y-axis labels */}
              <text x="45" y="245" textAnchor="end" className="text-xs fill-gray-600">0%</text>
              <text x="45" y="195" textAnchor="end" className="text-xs fill-gray-600">-5%</text>
              <text x="45" y="145" textAnchor="end" className="text-xs fill-gray-600">-10%</text>
              <text x="45" y="95" textAnchor="end" className="text-xs fill-gray-600">-15%</text>
              <text x="45" y="65" textAnchor="end" className="text-xs fill-gray-600">-20%</text>
              
              {/* X-axis labels */}
              <text x="60" y="260" textAnchor="middle" className="text-xs fill-gray-600">0</text>
              <text x="150" y="260" textAnchor="middle" className="text-xs fill-gray-600">12</text>
              <text x="250" y="260" textAnchor="middle" className="text-xs fill-gray-600">24</text>
              <text x="350" y="260" textAnchor="middle" className="text-xs fill-gray-600">36</text>
              <text x="440" y="260" textAnchor="middle" className="text-xs fill-gray-600">48</text>
              <text x="250" y="285" textAnchor="middle" className="text-xs fill-gray-600">Weeks</text>
              
              {/* Weight loss curve */}
              <path d="M 60 240 Q 150 200 250 120 T 440 70" 
                    fill="none" stroke="#22c55e" strokeWidth="3" />
              
              {/* No plateau indicator */}
              <rect x="320" y="50" width="100" height="40" fill="#dcfce7" stroke="#22c55e" strokeWidth="1" rx="5" />
              <text x="370" y="65" textAnchor="middle" className="text-xs font-medium fill-gray-700">No Plateau</text>
              <text x="370" y="80" textAnchor="middle" className="text-xs fill-gray-600">Continued loss</text>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 7: H — Cardiometabolic Reset
    {
      title: "H — Homeostatic Defence: Cardiometabolic Reset",
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-red-800 mb-3">Semaglutide SELECT Trial</h3>
            <div className="flex items-center space-x-4">
              <div className="text-3xl font-bold text-red-600">20%</div>
              <div>
                <p className="text-sm text-gray-700">↓ MACE (Major Adverse Cardiovascular Events)</p>
                <p className="text-sm font-medium text-gray-800">in people without diabetes</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Lipid Transport Effects</h3>
            <div className="flex items-center space-x-4">
              <div className="text-3xl font-bold text-blue-600">↓40%</div>
              <div>
                <p className="text-sm text-gray-700">ApoC-III on retatrutide</p>
                <p className="text-sm text-gray-600">Direct lipid-transport effects</p>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 500 300" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="250" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Cardiometabolic Protection Mechanisms
              </text>
              
              {/* Heart */}
              <path d="M 250 100 C 225 75 175 75 175 115 C 175 145 250 190 250 190 C 250 190 325 145 325 115 C 325 75 275 75 250 100 Z" 
                    fill="#fecaca" stroke="#ef4444" strokeWidth="2" />
              
              {/* Protection shields */}
              <ellipse cx="150" cy="120" rx="40" ry="60" fill="none" stroke="#3b82f6" strokeWidth="2" strokeDasharray="5,5" />
              <text x="150" y="200" textAnchor="middle" className="text-xs fill-gray-600">↓ Inflammation</text>
              
              <ellipse cx="350" cy="120" rx="40" ry="60" fill="none" stroke="#22c55e" strokeWidth="2" strokeDasharray="5,5" />
              <text x="350" y="200" textAnchor="middle" className="text-xs fill-gray-600">↓ Lipids</text>
              
              <ellipse cx="250" cy="120" rx="60" ry="80" fill="none" stroke="#f59e0b" strokeWidth="2" strokeDasharray="5,5" />
              <text x="250" y="230" textAnchor="middle" className="text-xs fill-gray-600">↓ Glucose</text>
              
              {/* Result box */}
              <rect x="150" y="250" width="200" height="30" fill="#dcfce7" stroke="#22c55e" strokeWidth="2" rx="5" />
              <text x="250" y="270" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                20% ↓ CV Death (Non-diabetics)
              </text>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 8: Glucagon Arm = Calorie-Independent Tweak
    {
      title: "Glucagon Arm = Calorie-Independent Tweak",
      content: (
        <div className="space-y-6">
          <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-orange-800 mb-3">Glucagon Receptor Stimulation</h3>
            <ul className="text-sm space-y-2 text-gray-700">
              <li>• Raises resting EE ≈ 150 kcal/d</li>
              <li>• Via BAT UCP-1 activation</li>
              <li>• Independent of caloric restriction</li>
            </ul>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 500 300" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="250" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Glucagon-Mediated Thermogenesis
              </text>
              
              {/* Glucagon receptor */}
              <rect x="50" y="80" width="100" height="60" fill="#fed7aa" stroke="#f97316" strokeWidth="2" rx="8" />
              <text x="100" y="105" textAnchor="middle" className="text-xs font-medium fill-gray-700">Glucagon</text>
              <text x="100" y="120" textAnchor="middle" className="text-xs font-medium fill-gray-700">Receptor</text>
              
              {/* Arrow to BAT */}
              <path d="M 150 110 L 220 110" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowGray)" />
              
              {/* BAT cells */}
              <g transform="translate(250, 110)">
                <rect x="-30" y="-40" width="60" height="80" fill="#7c2d12" stroke="#92400e" strokeWidth="2" rx="5" />
                <text x="0" y="-15" textAnchor="middle" className="text-xs fill-white">BAT</text>
                <circle cx="-10" cy="10" r="8" fill="#fed7aa" />
                <circle cx="10" cy="10" r="8" fill="#fed7aa" />
                <text x="0" y="35" textAnchor="middle" className="text-xs fill-white">UCP-1↑</text>
              </g>
              
              {/* Heat production */}
              <path d="M 310 90 Q 340 85 360 90" fill="none" stroke="#ef4444" strokeWidth="2" />
              <path d="M 310 110 Q 340 115 360 110" fill="none" stroke="#ef4444" strokeWidth="2" />
              <path d="M 310 130 Q 340 135 360 130" fill="none" stroke="#ef4444" strokeWidth="2" />
              <text x="380" y="115" className="text-xs fill-gray-600">Heat</text>
              
              {/* Energy expenditure box */}
              <rect x="150" y="180" width="200" height="60" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2" rx="5" />
              <text x="250" y="200" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                +150 kcal/day
              </text>
              <text x="250" y="220" textAnchor="middle" className="text-xs fill-gray-600">
                Resting Energy Expenditure
              </text>
              
              <defs>
                <marker id="arrowGray" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#6b7280" />
                </marker>
              </defs>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 9: I — NF-κB Kept Quiet
    {
      title: "I — Inflammation: NF-κB Kept Quiet",
      content: (
        <div className="space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">GLP-1RAs Suppress NF-κB</h3>
            <p className="text-sm text-gray-700 mb-3">
              Inflammatory markers fall within weeks:
            </p>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white p-2 rounded text-center">
                <p className="text-lg font-bold text-yellow-600">↓ CRP</p>
              </div>
              <div className="bg-white p-2 rounded text-center">
                <p className="text-lg font-bold text-yellow-600">↓ IL-6</p>
              </div>
              <div className="bg-white p-2 rounded text-center">
                <p className="text-lg font-bold text-yellow-600">↓ TNF-α</p>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 500 300" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="250" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                NF-κB Suppression Cascade
              </text>
              
              {/* GLP-1 receptor */}
              <rect x="50" y="100" width="80" height="40" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" rx="5" />
              <text x="90" y="125" textAnchor="middle" className="text-xs font-medium">GLP-1R</text>
              
              {/* Inhibition arrow */}
              <path d="M 130 120 L 200 120" stroke="#ef4444" strokeWidth="3" />
              <line x1="165" y1="110" x2="165" y2="130" stroke="#ef4444" strokeWidth="3" />
              
              {/* NF-κB */}
              <circle cx="250" cy="120" r="30" fill="#fecaca" stroke="#ef4444" strokeWidth="2" />
              <text x="250" y="125" textAnchor="middle" className="text-xs font-medium">NF-κB</text>
              
              {/* Downstream effects */}
              <path d="M 280 120 L 350 80" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowGray)" />
              <path d="M 280 120 L 350 120" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowGray)" />
              <path d="M 280 120 L 350 160" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowGray)" />
              
              {/* Cytokines */}
              <circle cx="380" cy="80" r="20" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2" />
              <text x="380" y="85" textAnchor="middle" className="text-xs">CRP↓</text>
              
              <circle cx="380" cy="120" r="20" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2" />
              <text x="380" y="125" textAnchor="middle" className="text-xs">IL-6↓</text>
              
              <circle cx="380" cy="160" r="20" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2" />
              <text x="380" y="165" textAnchor="middle" className="text-xs">TNF-α↓</text>
              
              {/* Timeline */}
              <rect x="150" y="220" width="200" height="40" fill="#dcfce7" stroke="#22c55e" strokeWidth="1" rx="5" />
              <text x="250" y="245" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Effect within weeks
              </text>
              
              <defs>
                <marker id="arrowGray" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#6b7280" />
                </marker>
              </defs>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 10: Neuro-Inflammation & Cognition
    {
      title: "Neuro-Inflammation & Cognition",
      content: (
        <div className="space-y-6">
          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Pre-clinical AD Studies</h3>
            <ul className="text-sm space-y-2 text-gray-700">
              <li>• Liraglutide preserves synapses</li>
              <li>• Improves maze performance</li>
              <li>• Phase 2 PD trials ongoing</li>
            </ul>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 500 300" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="250" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Neuroprotective Mechanisms
              </text>
              
              {/* Brain outline */}
              <ellipse cx="250" cy="150" rx="120" ry="90" fill="#f3e8ff" stroke="#a855f7" strokeWidth="2" />
              
              {/* Healthy neuron */}
              <g transform="translate(180, 120)">
                <circle cx="0" cy="0" r="15" fill="#22c55e" />
                <path d="M -15 0 L -30 -10 M -15 0 L -30 10 M 15 0 L 30 -10 M 15 0 L 30 10" 
                      stroke="#22c55e" strokeWidth="2" />
                <text x="0" y="30" textAnchor="middle" className="text-xs fill-gray-600">Preserved</text>
              </g>
              
              {/* Protected synapse */}
              <g transform="translate(320, 150)">
                <circle cx="-10" cy="0" r="8" fill="#3b82f6" />
                <circle cx="10" cy="0" r="8" fill="#3b82f6" />
                <path d="M -2 0 L 2 0" stroke="#3b82f6" strokeWidth="3" />
                <text x="0" y="25" textAnchor="middle" className="text-xs fill-gray-600">Synapse</text>
              </g>
              
              {/* GLP-1 effect */}
              <path d="M 100 150 L 130 150" stroke="#22c55e" strokeWidth="3" markerEnd="url(#arrowGreen)" />
              <text x="80" y="155" textAnchor="end" className="text-xs fill-gray-600">GLP-1</text>
              
              {/* Protection indicators */}
              <path d="M 200 80 Q 250 60 300 80" fill="none" stroke="#22c55e" strokeWidth="2" strokeDasharray="5,5" />
              <text x="250" y="55" textAnchor="middle" className="text-xs fill-gray-600">↓ Neuroinflammation</text>
              
              {/* Clinical trials box */}
              <rect x="150" y="230" width="200" height="40" fill="#e9d5ff" stroke="#a855f7" strokeWidth="2" rx="5" />
              <text x="250" y="250" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Phase 2 PD Trials Ongoing
              </text>
              
              <defs>
                <marker id="arrowGreen" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#22c55e" />
                </marker>
              </defs>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 11: F — Liver First
    {
      title: "F — Fibrosis: Liver First",
      content: (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Semaglutide 72-Week NASH Trial</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-3 rounded">
                <p className="text-2xl font-bold text-green-600">36.8%</p>
                <p className="text-sm text-gray-600">≥1-stage fibrosis regression</p>
                <p className="text-xs text-gray-500">Semaglutide</p>
              </div>
              <div className="bg-white p-3 rounded">
                <p className="text-2xl font-bold text-gray-600">22.4%</p>
                <p className="text-sm text-gray-600">≥1-stage fibrosis regression</p>
                <p className="text-xs text-gray-500">Placebo</p>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 500 300" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="250" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Hepatic Fibrosis Regression
              </text>
              
              {/* Liver outline */}
              <path d="M 150 100 Q 250 80 350 100 L 350 180 Q 250 220 150 180 Z" 
                    fill="#fed7aa" stroke="#92400e" strokeWidth="2" />
              
              {/* Fibrotic areas before */}
              <circle cx="200" cy="130" r="20" fill="#dc2626" opacity="0.6" />
              <circle cx="250" cy="150" r="25" fill="#dc2626" opacity="0.6" />
              <circle cx="300" cy="130" r="20" fill="#dc2626" opacity="0.6" />
              <text x="250" y="110" textAnchor="middle" className="text-xs font-medium fill-gray-700">Before</text>
              
              {/* Arrow */}
              <path d="M 250 240 L 250 270" stroke="#6b7280" strokeWidth="3" markerEnd="url(#arrowGray)" />
              <text x="280" y="260" className="text-xs fill-gray-600">GLP-1 Tx</text>
              
              {/* Liver after treatment */}
              <g transform="translate(0, 180)">
                <path d="M 150 100 Q 250 80 350 100 L 350 180 Q 250 220 150 180 Z" 
                      fill="#dcfce7" stroke="#22c55e" strokeWidth="2" />
                
                {/* Reduced fibrotic areas */}
                <circle cx="200" cy="130" r="10" fill="#fca5a5" opacity="0.4" />
                <circle cx="300" cy="130" r="10" fill="#fca5a5" opacity="0.4" />
                <text x="250" y="110" textAnchor="middle" className="text-xs font-medium fill-gray-700">After 72 weeks</text>
              </g>
              
              <defs>
                <marker id="arrowGray" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#6b7280" />
                </marker>
              </defs>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 12: Vasculature Too
    {
      title: "Vasculature Too",
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-red-800 mb-3">Vascular Protection</h3>
            <ul className="text-sm space-y-2 text-gray-700">
              <li>• Liraglutide blocks VSMC proliferation</li>
              <li>• → Slower arterial stiffening</li>
              <li>• Reduced atherosclerosis progression</li>
            </ul>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 500 300" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="250" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Vascular Smooth Muscle Cell (VSMC) Protection
              </text>
              
              {/* Blood vessel cross-section */}
              <g transform="translate(250, 150)">
                {/* Outer layer */}
                <ellipse cx="0" cy="0" rx="120" ry="80" fill="none" stroke="#92400e" strokeWidth="3" />
                
                {/* Smooth muscle layer - before */}
                <ellipse cx="0" cy="0" rx="100" ry="65" fill="#fed7aa" stroke="#f97316" strokeWidth="2" />
                
                {/* Inner layer */}
                <ellipse cx="0" cy="0" rx="60" ry="40" fill="#fecaca" stroke="#ef4444" strokeWidth="2" />
                
                {/* VSMCs */}
                <circle cx="-30" cy="-20" r="8" fill="#92400e" />
                <circle cx="30" cy="-20" r="8" fill="#92400e" />
                <circle cx="0" cy="20" r="8" fill="#92400e" />
                <circle cx="-40" cy="10" r="8" fill="#92400e" />
                <circle cx="40" cy="10" r="8" fill="#92400e" />
                
                <text x="0" y="0" textAnchor="middle" className="text-xs fill-white">Lumen</text>
              </g>
              
              {/* GLP-1 effect */}
              <path d="M 50 100 L 100 120" stroke="#22c55e" strokeWidth="3" markerEnd="url(#arrowGreen)" />
              <text x="40" y="95" textAnchor="end" className="text-xs fill-gray-600">Liraglutide</text>
              
              {/* Inhibition symbols */}
              <text x="180" y="100" className="text-2xl fill-red-600">⊗</text>
              <text x="300" y="100" className="text-2xl fill-red-600">⊗</text>
              <text x="240" y="220" className="text-2xl fill-red-600">⊗</text>
              
              {/* Results */}
              <rect x="300" y="250" width="180" height="30" fill="#dcfce7" stroke="#22c55e" strokeWidth="2" rx="5" />
              <text x="390" y="270" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                ↓ Arterial Stiffening
              </text>
              
              <defs>
                <marker id="arrowGreen" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#22c55e" />
                </marker>
              </defs>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 13: T — Circadian-Metabolic Coupling
    {
      title: "T — Time-Keeping: Circadian-Metabolic Coupling",
      content: (
        <div className="space-y-6">
          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Circadian GLP-1 Patterns</h3>
            <ul className="text-sm space-y-2 text-gray-700">
              <li>• Endogenous GLP-1 peaks morning</li>
              <li>• Pharmacologic dosing consolidates nocturnal glucose dips</li>
              <li>• Restores metabolic rhythm</li>
            </ul>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 500 300" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="250" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                24-Hour GLP-1 & Glucose Patterns
              </text>
              
              {/* Axes */}
              <line x1="60" y1="240" x2="440" y2="240" stroke="#6b7280" strokeWidth="2" />
              <line x1="60" y1="240" x2="60" y2="60" stroke="#6b7280" strokeWidth="2" />
              
              {/* Time labels */}
              <text x="60" y="260" textAnchor="middle" className="text-xs fill-gray-600">6am</text>
              <text x="150" y="260" textAnchor="middle" className="text-xs fill-gray-600">12pm</text>
              <text x="250" y="260" textAnchor="middle" className="text-xs fill-gray-600">6pm</text>
              <text x="350" y="260" textAnchor="middle" className="text-xs fill-gray-600">12am</text>
              <text x="440" y="260" textAnchor="middle" className="text-xs fill-gray-600">6am</text>
              
              {/* Natural GLP-1 curve (peaks morning) */}
              <path d="M 60 180 Q 100 120 150 140 T 250 200 Q 350 220 440 180" 
                    fill="none" stroke="#3b82f6" strokeWidth="2" strokeDasharray="5,5" />
              <text x="100" y="110" className="text-xs fill-blue-600">Natural GLP-1</text>
              
              {/* Pharmacologic GLP-1 (steady) */}
              <line x1="60" y1="150" x2="440" y2="150" stroke="#22c55e" strokeWidth="3" />
              <text x="380" y="140" className="text-xs fill-green-600">GLP-1 RA</text>
              
              {/* Glucose curve */}
              <path d="M 60 200 Q 150 180 250 190 Q 350 170 440 200" 
                    fill="none" stroke="#ef4444" strokeWidth="2" />
              <text x="300" y="160" className="text-xs fill-red-600">Glucose</text>
              
              {/* Night zone */}
              <rect x="300" y="60" width="140" height="180" fill="#6b7280" opacity="0.1" />
              <text x="370" y="80" textAnchor="middle" className="text-xs fill-gray-600">Night</text>
              
              {/* Key insight */}
              <rect x="120" y="270" width="260" height="25" fill="#e9d5ff" stroke="#a855f7" strokeWidth="1" rx="5" />
              <text x="250" y="288" textAnchor="middle" className="text-xs font-medium fill-gray-700">
                Consolidated nocturnal glucose control
              </text>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 14: Cellular Senescence Hints
    {
      title: "Cellular Senescence Hints",
      content: (
        <div className="space-y-6">
          <div className="bg-pink-50 border border-pink-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-pink-800 mb-3">Pre-clinical Findings</h3>
            <p className="text-sm text-gray-700">
              Adipose stem-cells exposed to GLP-1R signalling down-regulate p16<sup>INK4a</sup>
            </p>
            <p className="text-sm text-gray-600 mt-2">
              (Key senescence marker)
            </p>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 500 300" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="250" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Senescence Marker Regulation
              </text>
              
              {/* Adipose stem cell */}
              <circle cx="150" cy="150" r="50" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2" />
              <text x="150" y="145" textAnchor="middle" className="text-xs font-medium fill-gray-700">Adipose</text>
              <text x="150" y="160" textAnchor="middle" className="text-xs font-medium fill-gray-700">Stem Cell</text>
              
              {/* GLP-1R */}
              <rect x="120" y="80" width="60" height="30" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" rx="5" />
              <text x="150" y="100" textAnchor="middle" className="text-xs">GLP-1R</text>
              
              {/* Signaling arrow */}
              <path d="M 200 150 L 250 150" stroke="#22c55e" strokeWidth="3" markerEnd="url(#arrowGreen)" />
              
              {/* p16 regulation */}
              <g transform="translate(300, 150)">
                <rect x="-40" y="-30" width="80" height="60" fill="#fecaca" stroke="#ef4444" strokeWidth="2" rx="5" />
                <text x="0" y="-5" textAnchor="middle" className="text-sm font-medium fill-gray-700">p16<tspan baselineShift="super" fontSize="8">INK4a</tspan></text>
                <text x="0" y="15" textAnchor="middle" className="text-2xl fill-red-600">↓</text>
              </g>
              
              {/* Result */}
              <rect x="150" y="220" width="200" height="50" fill="#dcfce7" stroke="#22c55e" strokeWidth="2" rx="5" />
              <text x="250" y="240" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Reduced Senescence
              </text>
              <text x="250" y="255" textAnchor="middle" className="text-xs fill-gray-600">
                Cellular rejuvenation potential
              </text>
              
              <defs>
                <marker id="arrowGreen" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#22c55e" />
                </marker>
              </defs>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 15: Deep Dive – Retatrutide Chemistry & Receptor Balance
    {
      title: "Deep Dive – Retatrutide: Chemistry & Receptor Balance",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Molecular Design</h3>
            <ul className="text-sm space-y-2 text-gray-700">
              <li>• Single peptide, C18 diacid linker</li>
              <li>• Once-weekly PK profile</li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Receptor Potency Profile</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white p-2 rounded text-center">
                <p className="text-sm font-medium">GIP</p>
                <p className="text-lg font-bold text-green-600">0.9×</p>
                <p className="text-xs text-gray-600">endogenous</p>
              </div>
              <div className="bg-white p-2 rounded text-center">
                <p className="text-sm font-medium">GLP-1</p>
                <p className="text-lg font-bold text-blue-600">0.4×</p>
                <p className="text-xs text-gray-600">endogenous</p>
              </div>
              <div className="bg-white p-2 rounded text-center">
                <p className="text-sm font-medium">Glucagon</p>
                <p className="text-lg font-bold text-red-600">0.3×</p>
                <p className="text-xs text-gray-600">endogenous</p>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 500 250" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="250" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Retatrutide Structure & Activity
              </text>
              
              {/* Peptide backbone */}
              <path d="M 50 100 Q 100 80 150 100 T 250 100 Q 300 120 350 100 T 450 100" 
                    fill="none" stroke="#6b7280" strokeWidth="3" />
              
              {/* C18 linker */}
              <rect x="220" y="120" width="60" height="20" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2" rx="5" />
              <text x="250" y="135" textAnchor="middle" className="text-xs">C18</text>
              
              {/* Receptor binding sites */}
              <circle cx="100" cy="100" r="15" fill="#22c55e" />
              <text x="100" y="105" textAnchor="middle" className="text-xs fill-white">GIP</text>
              
              <circle cx="250" cy="100" r="15" fill="#3b82f6" />
              <text x="250" y="105" textAnchor="middle" className="text-xs fill-white">GLP-1</text>
              
              <circle cx="400" cy="100" r="15" fill="#ef4444" />
              <text x="400" y="105" textAnchor="middle" className="text-xs fill-white">GCG</text>
              
              {/* Half-life indicator */}
              <rect x="150" y="180" width="200" height="40" fill="#e9d5ff" stroke="#a855f7" strokeWidth="2" rx="5" />
              <text x="250" y="195" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Once-Weekly Dosing
              </text>
              <text x="250" y="210" textAnchor="middle" className="text-xs fill-gray-600">
                Extended half-life design
              </text>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 16: Mechanistic Edge
    {
      title: "Mechanistic Edge",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4">
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-red-800 mb-2">Glucagon arm</h3>
              <p className="text-sm text-gray-700">→ ↑ β-oxidation + BAT thermogenesis</p>
            </div>
            
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-green-800 mb-2">GIP arm</h3>
              <p className="text-sm text-gray-700">Blunts nausea, amplifies β-cell cAMP</p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-blue-800 mb-2">Net result</h3>
              <p className="text-sm text-gray-700">Insulin-SENSITIVITY gains independent of weight loss</p>
            </div>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 500 300" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="250" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Triple Agonist Synergy
              </text>
              
              {/* Three receptor pathways */}
              <g transform="translate(100, 100)">
                <rect x="-40" y="-30" width="80" height="60" fill="#fecaca" stroke="#ef4444" strokeWidth="2" rx="5" />
                <text x="0" y="-5" textAnchor="middle" className="text-xs font-medium">Glucagon</text>
                <text x="0" y="10" textAnchor="middle" className="text-xs">β-oxidation↑</text>
                <text x="0" y="25" textAnchor="middle" className="text-xs">BAT↑</text>
              </g>
              
              <g transform="translate(250, 100)">
                <rect x="-40" y="-30" width="80" height="60" fill="#dcfce7" stroke="#22c55e" strokeWidth="2" rx="5" />
                <text x="0" y="-5" textAnchor="middle" className="text-xs font-medium">GIP</text>
                <text x="0" y="10" textAnchor="middle" className="text-xs">Nausea↓</text>
                <text x="0" y="25" textAnchor="middle" className="text-xs">β-cell↑</text>
              </g>
              
              <g transform="translate(400, 100)">
                <rect x="-40" y="-30" width="80" height="60" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" rx="5" />
                <text x="0" y="-5" textAnchor="middle" className="text-xs font-medium">GLP-1</text>
                <text x="0" y="10" textAnchor="middle" className="text-xs">Satiety↑</text>
                <text x="0" y="25" textAnchor="middle" className="text-xs">Glucose↓</text>
              </g>
              
              {/* Convergence arrows */}
              <path d="M 140 130 L 210 180" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowGray)" />
              <path d="M 250 130 L 250 180" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowGray)" />
              <path d="M 360 130 L 290 180" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowGray)" />
              
              {/* Central result */}
              <rect x="150" y="200" width="200" height="60" fill="#f3e8ff" stroke="#a855f7" strokeWidth="3" rx="5" />
              <text x="250" y="225" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Insulin Sensitivity
              </text>
              <text x="250" y="245" textAnchor="middle" className="text-xs fill-gray-600">
                Independent of weight loss
              </text>
              
              <defs>
                <marker id="arrowGray" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#6b7280" />
                </marker>
              </defs>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 17: SHIFT Scorecard
    {
      title: "SHIFT Scorecard",
      content: (
        <div className="space-y-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Pillar</th>
                  <th className="px-6 py-3 text-center text-sm font-medium text-gray-700">S</th>
                  <th className="px-6 py-3 text-center text-sm font-medium text-gray-700">H</th>
                  <th className="px-6 py-3 text-center text-sm font-medium text-gray-700">I</th>
                  <th className="px-6 py-3 text-center text-sm font-medium text-gray-700">F</th>
                  <th className="px-6 py-3 text-center text-sm font-medium text-gray-700">T</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Semaglutide</td>
                  <td className="px-6 py-4 text-center text-lg">▲</td>
                  <td className="px-6 py-4 text-center text-lg">▲</td>
                  <td className="px-6 py-4 text-center text-lg">▲</td>
                  <td className="px-6 py-4 text-center text-lg">▲</td>
                  <td className="px-6 py-4 text-center text-lg">△</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Tirzepatide</td>
                  <td className="px-6 py-4 text-center text-lg">▲</td>
                  <td className="px-6 py-4 text-center text-lg text-green-600">▲▲</td>
                  <td className="px-6 py-4 text-center text-lg">▲</td>
                  <td className="px-6 py-4 text-center text-lg">▲</td>
                  <td className="px-6 py-4 text-center text-lg">△</td>
                </tr>
                <tr className="bg-blue-50">
                  <td className="px-6 py-4 text-sm font-bold text-gray-900">Retatrutide</td>
                  <td className="px-6 py-4 text-center text-lg font-bold text-green-600">▲▲</td>
                  <td className="px-6 py-4 text-center text-lg font-bold text-green-600">▲▲</td>
                  <td className="px-6 py-4 text-center text-lg font-bold text-green-600">▲▲</td>
                  <td className="px-6 py-4 text-center text-lg font-bold text-green-600">▲▲</td>
                  <td className="px-6 py-4 text-center text-lg font-bold text-green-600">▲</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <p className="text-sm text-gray-600 text-center">
              ▲ = good, ▲▲ = best in class – expert consensus + trial data
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-2">Key Insight</h3>
            <p className="text-sm text-gray-700">
              Retatrutide shows superior performance across all SHIFT pillars, 
              making it the most comprehensive longevity intervention in the GLP-1 class.
            </p>
          </div>
        </div>
      )
    },

    // Slide 18: Fiber × GLP-1 Synergy
    {
      title: "Fiber × GLP-1 Synergy",
      content: (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">SCFAs Fuel the Loop</h3>
            <ul className="text-sm space-y-2 text-gray-700">
              <li>• Soluble fiber → acetate/propionate</li>
              <li>• → GPR43 on L-cells</li>
              <li>• → endogenous GLP-1 surge</li>
              <li>• Fiber also mitigates GLP-1-induced constipation & helps preserve lean mass</li>
            </ul>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 500 300" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="250" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Fiber-SCFA-GLP-1 Cascade
              </text>
              
              {/* Fiber */}
              <rect x="50" y="100" width="80" height="40" fill="#dcfce7" stroke="#22c55e" strokeWidth="2" rx="5" />
              <text x="90" y="125" textAnchor="middle" className="text-xs font-medium">Soluble Fiber</text>
              
              {/* Arrow to SCFAs */}
              <path d="M 130 120 L 180 120" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowGray)" />
              
              {/* SCFAs */}
              <g transform="translate(220, 120)">
                <circle cx="-15" cy="-10" r="12" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2" />
                <text x="-15" y="-7" textAnchor="middle" className="text-xs">Ac</text>
                
                <circle cx="15" cy="-10" r="12" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2" />
                <text x="15" y="-7" textAnchor="middle" className="text-xs">Pr</text>
                
                <text x="0" y="15" textAnchor="middle" className="text-xs fill-gray-600">SCFAs</text>
              </g>
              
              {/* Arrow to GPR43 */}
              <path d="M 260 120 L 310 120" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowGray)" />
              
              {/* L-cell with GPR43 */}
              <g transform="translate(350, 120)">
                <circle cx="0" cy="0" r="25" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" />
                <text x="0" y="-5" textAnchor="middle" className="text-xs font-medium">L-cell</text>
                <rect x="-15" y="5" width="30" height="15" fill="#3b82f6" rx="3" />
                <text x="0" y="15" textAnchor="middle" className="text-xs fill-white">GPR43</text>
              </g>
              
              {/* GLP-1 release */}
              <path d="M 375 120 L 420 120" stroke="#22c55e" strokeWidth="3" markerEnd="url(#arrowGreen)" />
              <text x="430" y="125" className="text-sm font-medium fill-green-600">GLP-1↑</text>
              
              {/* Benefits box */}
              <rect x="100" y="180" width="300" height="80" fill="#f3e8ff" stroke="#a855f7" strokeWidth="2" rx="5" />
              <text x="250" y="200" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Synergistic Benefits
              </text>
              <text x="250" y="220" textAnchor="middle" className="text-xs fill-gray-600">
                • Enhanced endogenous GLP-1
              </text>
              <text x="250" y="235" textAnchor="middle" className="text-xs fill-gray-600">
                • Reduced GI side effects
              </text>
              <text x="250" y="250" textAnchor="middle" className="text-xs fill-gray-600">
                • Lean mass preservation
              </text>
              
              <defs>
                <marker id="arrowGray" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#6b7280" />
                </marker>
                <marker id="arrowGreen" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#22c55e" />
                </marker>
              </defs>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 19: Protocol You Can Use Monday
    {
      title: "Protocol You Can Use Monday",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-xl font-medium text-blue-800 mb-4">Clinical Implementation Protocol</h3>
            
            <div className="space-y-4">
              <div className="bg-white p-4 rounded">
                <h4 className="font-medium text-gray-800 mb-2">Fiber Supplementation</h4>
                <p className="text-lg font-bold text-blue-600">30 g/d mixed soluble fiber</p>
                <p className="text-sm text-gray-600">Psyllium + inulin combination</p>
              </div>
              
              <div className="bg-white p-4 rounded">
                <h4 className="font-medium text-gray-800 mb-2">Protein Requirements</h4>
                <p className="text-lg font-bold text-green-600">≥1.6 g protein/kg body weight</p>
                <p className="text-sm text-gray-600">Pair with resistance training</p>
              </div>
              
              <div className="bg-white p-4 rounded">
                <h4 className="font-medium text-gray-800 mb-2">Monitoring Schedule</h4>
                <p className="text-sm text-gray-700">Every 12 weeks:</p>
                <ul className="text-sm text-gray-600 ml-4 mt-1">
                  <li>• B12 levels</li>
                  <li>• Ferritin</li>
                  <li>• HRV (Heart Rate Variability)</li>
                  <li>• ALT (liver function)</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-yellow-800 mb-2">Pro Tip</h3>
            <p className="text-sm text-gray-700">
              Start fiber supplementation 1 week before GLP-1 initiation to minimize GI upset
            </p>
          </div>
        </div>
      )
    },

    // Slide 20: Risks & Mitigations
    {
      title: "Clinical Caveats: Risks & Mitigations",
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-red-800 mb-3">Key Risks & Solutions</h3>
            
            <div className="space-y-4">
              <div className="bg-white p-3 rounded">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-800">Lean mass ↓3–8%</h4>
                    <p className="text-sm text-gray-700 mt-1">
                      <strong>Mitigation:</strong> Prescribe progressive overload + HMB if &gt;50 yrs
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-3 rounded">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-800">Early GI upset</h4>
                    <p className="text-sm text-gray-700 mt-1">
                      <strong>Mitigation:</strong> Low-FODMAP ramp, split fiber doses
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-3 rounded">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-800">Rare gallstones</h4>
                    <p className="text-sm text-gray-700 mt-1">
                      <strong>Mitigation:</strong> Schedule RUQ US if ALT rises &gt;2× baseline
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Clinical Pearl</h3>
            <p className="text-sm text-gray-700">
              Most side effects are front-loaded. Slow titration + fiber pre-loading dramatically improves tolerability.
            </p>
          </div>
        </div>
      )
    },

    // Slide 21: Beyond Retatrutide
    {
      title: "Looking Ahead: Beyond Retatrutide",
      content: (
        <div className="space-y-6">
          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Next Generation Pipeline</h3>
            
            <div className="space-y-3">
              <div className="bg-white p-3 rounded">
                <h4 className="font-medium text-purple-700">Quad-agonists</h4>
                <p className="text-sm text-gray-700">GLP-1/GIP/GCG/Amylin in primate studies</p>
              </div>
              
              <div className="bg-white p-3 rounded">
                <h4 className="font-medium text-purple-700">Oral small-molecule GLP-1 mimetics</h4>
                <p className="text-sm text-gray-700">Entering Phase 2 trials</p>
              </div>
              
              <div className="bg-white p-3 rounded">
                <h4 className="font-medium text-purple-700">SHIFT "poly-agonist scores"</h4>
                <p className="text-sm text-gray-700">To guide personalized selection</p>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 500 250" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="250" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Evolution of Multi-Agonist Therapy
              </text>
              
              {/* Timeline */}
              <line x1="50" y1="100" x2="450" y2="100" stroke="#6b7280" strokeWidth="2" />
              
              {/* Current */}
              <g transform="translate(100, 100)">
                <circle cx="0" cy="0" r="30" fill="#22c55e" stroke="#16a34a" strokeWidth="2" />
                <text x="0" y="5" textAnchor="middle" className="text-xs font-medium fill-white">Triple</text>
                <text x="0" y="-40" textAnchor="middle" className="text-xs fill-gray-600">2024</text>
              </g>
              
              {/* Near future */}
              <g transform="translate(250, 100)">
                <circle cx="0" cy="0" r="35" fill="#3b82f6" stroke="#2563eb" strokeWidth="2" />
                <text x="0" y="5" textAnchor="middle" className="text-xs font-medium fill-white">Quad</text>
                <text x="0" y="-45" textAnchor="middle" className="text-xs fill-gray-600">2026</text>
              </g>
              
              {/* Future */}
              <g transform="translate(400, 100)">
                <circle cx="0" cy="0" r="40" fill="#a855f7" stroke="#9333ea" strokeWidth="2" />
                <text x="0" y="0" textAnchor="middle" className="text-xs font-medium fill-white">Oral</text>
                <text x="0" y="12" textAnchor="middle" className="text-xs font-medium fill-white">Small Mol</text>
                <text x="0" y="-50" textAnchor="middle" className="text-xs fill-gray-600">2028+</text>
              </g>
              
              {/* SHIFT scoring */}
              <rect x="150" y="180" width="200" height="40" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2" rx="5" />
              <text x="250" y="200" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Personalized SHIFT Scoring
              </text>
              <text x="250" y="215" textAnchor="middle" className="text-xs fill-gray-600">
                Biomarker-guided selection
              </text>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 22: Three Take-Homes
    {
      title: "Three Take-Homes",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-300 p-6 rounded-lg">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center text-lg font-bold">
                    1
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Every SHIFT dial moves</h3>
                  <p className="text-gray-700">
                    With GLP-1s; that's why weight loss is just a footnote.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-300 p-6 rounded-lg">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center text-lg font-bold">
                    2
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Retatrutide breakthrough</h3>
                  <p className="text-gray-700">
                    May be the first therapy to cross the 30% weight-loss line <strong>and</strong> extend healthspan.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-300 p-6 rounded-lg">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center text-lg font-bold">
                    3
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Fiber is the force-multiplier</h3>
                  <p className="text-gray-700">
                    The cheap force-multiplier—don't run GLP-1s without it.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg mt-8">
            <p className="text-center text-lg font-medium text-gray-800">
              Remember: This isn't about weight loss—it's about cellular rejuvenation
            </p>
          </div>
        </div>
      )
    },

    // Slide 23: Invitation
    {
      title: "Invitation",
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center space-y-8">
          <div className="max-w-3xl">
            <blockquote className="text-2xl font-light italic text-gray-700 mb-8">
              "If you want the full PLAYBOOK for integrating the SHIFT framework in clinic, 
              join my mentorship."
            </blockquote>
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-lg shadow-lg">
            <h3 className="text-3xl font-bold mb-4">Limited Time Offer</h3>
            <div className="space-y-3">
              <p className="text-xl">First movers get:</p>
              <p className="text-2xl font-bold">$1k off membership</p>
              <p className="text-xl">+</p>
              <p className="text-2xl font-bold">10% off consulting for three months</p>
            </div>
          </div>

          <div className="flex justify-center mt-8">
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <QrCode className="w-32 h-32 text-gray-800" />
              <p className="text-sm text-gray-600 mt-2">Scan to join</p>
            </div>
          </div>

          <div className="text-lg text-gray-600">
            Transform your practice with evidence-based longevity protocols
          </div>
        </div>
      )
    },

    // Slide 24: Key References
    {
      title: "Key References",
      content: (
        <div className="space-y-4">
          <h3 className="text-xl font-medium text-gray-800 mb-4">Key References</h3>
          
          <div className="bg-gray-50 p-4 rounded-lg overflow-y-auto max-h-96">
            <ol className="space-y-3 text-sm text-gray-700">
              <li>1. Retatrutide Phase 2 obesity trial. <a href="https://www.nejm.org/doi/full/10.1056/NEJMoa2301972" className="text-blue-600 hover:underline">nejm.org</a></li>
              <li>2. SELECT cardiovascular-outcome trial (semaglutide). <a href="https://www.nejm.org/doi/full/10.1056/NEJMoa2307563" className="text-blue-600 hover:underline">nejm.org</a></li>
              <li>3. GLP-1 anti-inflammatory review. <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC10230051/" className="text-blue-600 hover:underline">pmc.ncbi.nlm.nih.gov</a></li>
              <li>4. Semaglutide NASH fibrosis trial. <a href="https://www.nejm.org/doi/full/10.1056/NEJMoa2413258" className="text-blue-600 hover:underline">nejm.org</a></li>
              <li>5. GLP-1 → BAT thermogenesis review. <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC10218520/" className="text-blue-600 hover:underline">pmc.ncbi.nlm.nih.gov</a></li>
              <li>6. Fiber-SCFA-GLP-1 mechanics. <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC3266401/" className="text-blue-600 hover:underline">pmc.ncbi.nlm.nih.gov</a></li>
              <li>7. Circadian rhythm of GLP-1. <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC9755352/" className="text-blue-600 hover:underline">pmc.ncbi.nlm.nih.gov</a></li>
              <li>8. VSMC anti-fibrotic study. <a href="https://pubmed.ncbi.nlm.nih.gov/28445811/" className="text-blue-600 hover:underline">pubmed.ncbi.nlm.nih.gov</a></li>
              <li>9. Retatrutide mechanistic analysis. <a href="https://www.nature.com/articles/s41591-024-03018-2" className="text-blue-600 hover:underline">nature.com</a></li>
              <li>10. Early retatrutide PK/PD in T2D. <a href="https://pubmed.ncbi.nlm.nih.gov/36354040/" className="text-blue-600 hover:underline">pubmed.ncbi.nlm.nih.gov</a></li>
              <li>11. Fiber receptor GPR43 in L-cells. <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC4046487/" className="text-blue-600 hover:underline">pmc.ncbi.nlm.nih.gov</a></li>
            </ol>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
            <p className="text-sm text-gray-700 italic">
              <strong>Note:</strong> Full PDFs linked in speaker notes.
            </p>
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link href="/mentorship">
              <a className="text-gray-500 hover:text-gray-700 flex items-center">
                <Home className="w-4 h-4 mr-1" />
                Mentorship
              </a>
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium">Microdosing GLP-1s</span>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm">
          {/* Slide Header */}
          <div className="border-b px-6 py-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-medium text-gray-900">
                {slides[currentSlide].title}
              </h2>
              <div className="text-sm text-gray-500">
                Slide {currentSlide + 1} of {slides.length}
              </div>
            </div>
          </div>

          {/* Slide Content */}
          <div className="p-8 min-h-[600px]">
            {slides[currentSlide].content}
          </div>

          {/* Navigation */}
          <div className="border-t px-6 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={prevSlide}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={currentSlide === 0}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </button>

              <div className="flex space-x-1">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full ${
                      index === currentSlide ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={nextSlide}
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                disabled={currentSlide === slides.length - 1}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}